/** @format */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { NgxFudisModule } from '@funidata/ngx-fudis';
import { UIRouterModule } from '@uirouter/angular';
import { ValidatablePlan } from 'common-typescript';
import {
    CourseUnit,
    Education,
    Enrolment,
    OtmId,
    Plan,
    StudyPeriod,
    StudyPeriodLocator,
    StudyRight,
    StudyYear,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import {
    combineLatest,
    combineLatestWith,
    distinctUntilChanged,
    forkJoin,
    from,
    map,
    Observable,
    of,
    ReplaySubject,
    shareReplay,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { SisCommonModule } from 'sis-common/sis-common.module';
import { COLOR_SERVICE, COMMON_PLAN_SERVICE, COMMON_STUDY_PERIOD_SERVICE } from 'sis-components/ajs-upgraded-modules';
import { AppErrorHandler } from 'sis-components/error-handler/app-error-handler';
import { EducationEntityService } from 'sis-components/service/education-entity.service';
import { PlanEntityService } from 'sis-components/service/plan-entity.service';
import { StudyPeriodLocatorService } from 'sis-components/service/study-period-locator.service';
import { StudyYearsEntityService } from 'sis-components/service/study-years-entity.service';
import { UniversityService } from 'sis-components/service/university.service';
import { SisComponentsModule } from 'sis-components/sis-components.module';
import { convertAJSPromiseToNative } from 'sis-components/util/utils';
import { COURSE_UNIT_INFO_MODAL } from '../../../ajs-upgraded-modules';
import { EnrolmentStudentService } from '../../../common/service/enrolment-student.service';
import { MyStudyRightService } from '../../../common/service/my-study-right.service';
import { PlanLoaderService } from '../../../common/service/plan-loader.service';
import { PlanSeekerService } from '../../../common/service/plan-seeker.service';
import { ChoosePlanComponent } from '../choose-plan/choose-plan.component';

interface CourseUnitsNotInCalendarData {
    allStudentPlans: Plan[];
    selectedValidatablePlan: ValidatablePlan;
    planEducations: Education[];
    notScheduledCourseUnitsByPeriod: Record<StudyPeriodLocator, CourseUnit[]>;
    courseUnitColorCategoriesById: Record<OtmId, string>;
    studyPeriodsByLocator: Record<StudyPeriodLocator, StudyPeriod>;
    studyYearsByStartYear: Record<number, StudyYear>;
}
@Component({
    selector: 'app-course-units-not-in-calendar',
    templateUrl: './course-units-not-in-calendar.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, NgxFudisModule, SisComponentsModule, SisCommonModule, ChoosePlanComponent, UIRouterModule],
})
export class CourseUnitsNotInCalendarComponent implements OnInit {
    planEntityService = inject(PlanEntityService);
    enrolmentStudentService = inject(EnrolmentStudentService);
    educationEntityService = inject(EducationEntityService);
    appErrorHandler = inject(AppErrorHandler);
    commonPlanService = inject(COMMON_PLAN_SERVICE);
    colorService = inject(COLOR_SERVICE);
    commonStudyPeriodService = inject(COMMON_STUDY_PERIOD_SERVICE);
    studyPeriodLocatorService = inject(StudyPeriodLocatorService);
    studyYearsEntityService = inject(StudyYearsEntityService);
    universityService = inject(UniversityService);
    courseUnitInfoModalService = inject(COURSE_UNIT_INFO_MODAL);
    myStudyRightService = inject(MyStudyRightService);
    planLoaderService = inject(PlanLoaderService);
    planSeekerService = inject(PlanSeekerService);

    planId$ = new ReplaySubject<OtmId | null>(null);
    data$: Observable<CourseUnitsNotInCalendarData>;

    ngOnInit() {
        this.data$ = this.createDataObservable();
    }

    createDataObservable(): Observable<CourseUnitsNotInCalendarData> {
        const allStudentEnrolments$ = this.createAllStudentEnrolmentsObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }));
        const allStudentStudyRights$: Observable<StudyRight[]> = this.createAllStudentStudyRightsObservable().pipe(
            shareReplay({ bufferSize: 1, refCount: true }),
        );
        const allStudentPlans$ = this.createAllStudentPlansObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }));
        // Resolves initial planId and after that emits values set to planId$
        const currentPlanId$ = this.createCurrentPlanIdObservable(this.planId$, allStudentPlans$, allStudentStudyRights$);
        const inputDataObservable$ = this.createInputDataObservable(
            allStudentPlans$,
            allStudentStudyRights$,
            currentPlanId$,
            allStudentEnrolments$,
        );

        return inputDataObservable$.pipe(
            switchMap(data => {
                const [allStudentPlans, allStudentStudyRights, planId, allStudentEnrolments] = data;
                return forkJoin({
                    selectedValidatablePlan: this.createValidatablePlanObservable(planId, allStudentPlans, allStudentStudyRights).pipe(
                        take(1),
                    ),
                    planEducations: this.createPlanEducationsObservable(allStudentPlans).pipe(take(1)),
                    enrolmentsInCalendar: of(this.findEnrolmentsInCalendar(allStudentEnrolments)),
                }).pipe(
                    map(result => ({
                        ...result,
                        allStudentPlans,
                    })),
                );
            }),
            switchMap(data =>
                this.createNotScheduledCourseUnitsObservable(data.enrolmentsInCalendar, data.selectedValidatablePlan).pipe(
                    take(1),
                    map(notScheduledCourseUnits => {
                        const { enrolmentsInCalendar, ...rest } = data;
                        return {
                            ...rest,
                            notScheduledCourseUnits,
                        };
                    }),
                ),
            ),
            switchMap(data => {
                const { notScheduledCourseUnits, selectedValidatablePlan, ...rest } = data;
                const notScheduledCourseUnitsByPeriod = this.getNotScheduledCourseUnitsByPeriod(
                    notScheduledCourseUnits,
                    selectedValidatablePlan,
                );
                const courseUnitColorCategoriesById = this.createCourseUnitColorCategoriesById(
                    notScheduledCourseUnits,
                    selectedValidatablePlan,
                );
                return forkJoin({
                    courseUnitColorCategoriesById: of(courseUnitColorCategoriesById),
                    notScheduledCourseUnitsByPeriod: of(notScheduledCourseUnitsByPeriod),
                    studyPeriodsByLocator: this.createStudyPeriodsByLocatorObservable(notScheduledCourseUnitsByPeriod).pipe(take(1)),
                }).pipe(
                    map(result => ({
                        selectedValidatablePlan,
                        ...rest,
                        ...result,
                    })),
                );
            }),
            switchMap(data =>
                this.createStudyYearsByStartYearObservable(data.studyPeriodsByLocator).pipe(
                    take(1),
                    map(studyYearsByStartYear => ({
                        ...data,
                        studyYearsByStartYear,
                    })),
                ),
            ),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    createCurrentPlanIdObservable(
        planId$: ReplaySubject<OtmId | null>,
        allStudentPlans$: Observable<Plan[]>,
        allStudentStudyRights$: Observable<StudyRight[]>,
    ): Observable<OtmId | null> {
        return combineLatest([allStudentPlans$.pipe(take(1)), allStudentStudyRights$.pipe(take(1))]).pipe(
            map(([allStudentPlans, allStudentStudyRights]): OtmId | null =>
                this.planSeekerService.resolvePlan(allStudentPlans, allStudentStudyRights),
            ),
            tap(planId => planId$.next(planId)),
            switchMap(() => planId$.asObservable()),
        );
    }

    createInputDataObservable(
        allStudentPlans$: Observable<Plan[]>,
        allStudentStudyRights$: Observable<StudyRight[]>,
        currentPlanId$: Observable<OtmId | null>,
        allStudentEnrolments$: Observable<Enrolment[]>,
    ): Observable<[Plan[], StudyRight[], OtmId | null, Enrolment[]]> {
        return combineLatest([allStudentPlans$, allStudentStudyRights$, currentPlanId$, allStudentEnrolments$]).pipe(
            distinctUntilChanged((current, previous) => {
                const [curPlans, curStudyRights, curPlanId, curEnrolments] = current;
                const [prevPlans, prevStudyRights, prevPlanId, prevEnrolments] = previous;
                return (
                    _.isEqual(curPlans, prevPlans) &&
                    _.isEqual(curStudyRights, prevStudyRights) &&
                    curPlanId === prevPlanId &&
                    _.isEqual(curEnrolments, prevEnrolments)
                );
            }),
        );
    }

    createValidatablePlanObservable(
        planId: OtmId | null,
        allStudentPlans: Plan[],
        allStudentStudyRights: StudyRight[],
    ): Observable<ValidatablePlan | null> {
        if (planId) {
            const resolvedPlan = allStudentPlans.find(plan => plan.id === planId);
            if (!resolvedPlan) {
                return of(null);
            }
            const studyRightId = allStudentStudyRights.find(sr => resolvedPlan.rootId === sr.educationId)?.id;
            return this.planLoaderService.createValidatablePlan(resolvedPlan.id, studyRightId).pipe(take(1));
        }
        return of(null);
    }

    createAllStudentPlansObservable(): Observable<Plan[]> {
        return this.planEntityService.getMyPlans();
    }

    createPlanEducationsObservable(allStudentPlans: Plan[]): Observable<Education[]> {
        const educationIds = _.compact(_.uniq(allStudentPlans.map(plan => plan.rootId)));
        if (educationIds.length > 0) {
            return this.educationEntityService.getByIds(educationIds);
        }
        return of([]);
    }

    createAllStudentEnrolmentsObservable(): Observable<Enrolment[]> {
        return this.enrolmentStudentService.getAllEnrolments();
    }

    createAllStudentStudyRightsObservable(): Observable<StudyRight[]> {
        return this.myStudyRightService.studyRights$.pipe(map(studyRights => [...studyRights]));
    }

    findEnrolmentsInCalendar(allStudentEnrolments: Enrolment[]): Enrolment[] {
        return allStudentEnrolments.filter(enrolment => enrolment.isInCalendar);
    }

    createNotScheduledCourseUnitsObservable(
        enrolmentsInCalendar: Enrolment[],
        validatablePlan: ValidatablePlan | null,
    ): Observable<CourseUnit[]> {
        if (validatablePlan) {
            // TODO: This returns course units that are enriched with '$hasTeaching' property. This
            //  is still used in the template, make sure to handle it when this method is angularized
            return from(
                convertAJSPromiseToNative(this.commonPlanService.findNotScheduledCourseUnits(validatablePlan, enrolmentsInCalendar)),
            ) as Observable<CourseUnit[]>;
        }
        return of([]);
    }

    createCourseUnitColorCategoriesById(
        notScheduledCourseUnits: CourseUnit[],
        validatablePlan: ValidatablePlan | null,
    ): Record<OtmId, string> {
        const colorCategoriesById: Record<OtmId, string> = {};
        if (validatablePlan) {
            notScheduledCourseUnits.forEach(courseUnit => {
                colorCategoriesById[courseUnit.id] = this.colorService.getCourseUnitColorCategory(validatablePlan, courseUnit);
            });
        }
        return colorCategoriesById;
    }

    createNotScheduledCourseUnitsByPeriodObservable(
        notScheduledCourseUnits$: Observable<CourseUnit[]>,
        validatablePlan$: Observable<ValidatablePlan | null>,
    ): Observable<Record<StudyPeriodLocator, CourseUnit[]>> {
        return notScheduledCourseUnits$.pipe(
            combineLatestWith(validatablePlan$),
            map(([notScheduledCourseUnits, validatablePlan]) =>
                this.getNotScheduledCourseUnitsByPeriod(notScheduledCourseUnits, validatablePlan),
            ),
        );
    }

    createStudyPeriodsByLocatorObservable(
        notScheduledCourseUnitsByPeriod: Record<StudyPeriodLocator, CourseUnit[]>,
    ): Observable<Record<StudyPeriodLocator, StudyPeriod>> {
        const studyPeriodIds = _.uniq(_.compact(_.keys(notScheduledCourseUnitsByPeriod))).filter(locator => locator !== 'undefined');
        if (studyPeriodIds.length > 0) {
            return from(Promise.all(studyPeriodIds.map(studyPeriodId => this.commonStudyPeriodService.getPeriodsFor(studyPeriodId)))).pipe(
                map(studyPeriods => _.keyBy(studyPeriods, 'locator') as Record<StudyPeriodLocator, StudyPeriod>),
            );
        }
        return of({});
    }

    createStudyYearsByStartYearObservable(
        studyPeriodsByLocator: Record<StudyPeriodLocator, StudyPeriod>,
    ): Observable<Record<number, StudyYear>> {
        const parsedLocators = _.chain(_.keys(studyPeriodsByLocator))
            .uniq()
            .map(locator => this.parseStudyPeriodLocator(locator))
            .value();

        if (!_.isEmpty(parsedLocators)) {
            const organisationId = this.universityService.getCurrentUniversityOrgId();
            const years = _.map(parsedLocators, 'year');
            const firstYear = _.min(years);
            const numOfYears = _.max(years) - firstYear + 1;
            return this.studyYearsEntityService
                .getStudyYears(organisationId, firstYear, numOfYears)
                .pipe(map(studyYears => _.keyBy(studyYears, 'startYear')));
        }
        return of({});
    }

    getNotScheduledCourseUnitsByPeriod(
        notScheduledCourseUnits: CourseUnit[],
        validatablePlan: ValidatablePlan | null,
    ): Record<OtmId, CourseUnit[]> {
        const courseUnitsByPeriods = _.groupBy(notScheduledCourseUnits, courseUnit => {
            if (validatablePlan) {
                return _.head(validatablePlan.getPlannedPeriods(courseUnit));
            }
            return undefined;
        });
        const notScheduledCourseUnitsByPeriod: Record<OtmId, CourseUnit[]> = {};
        _.chain(courseUnitsByPeriods)
            .keys()
            .sortBy()
            .forEach(key => {
                notScheduledCourseUnitsByPeriod[key] = courseUnitsByPeriods[key];
            })
            .value();
        return notScheduledCourseUnitsByPeriod;
    }

    parseStudyPeriodLocator(locator: StudyPeriodLocator) {
        return this.studyPeriodLocatorService.parseStudyPeriodLocator(locator);
    }

    onPlanChange(planId: OtmId) {
        this.planId$.next(planId);
    }

    onOpenCourseUnitModal(courseUnit: CourseUnit, validatablePlan: ValidatablePlan) {
        const colorCategory = this.colorService.getCourseUnitColorCategory(validatablePlan, courseUnit);
        this.courseUnitInfoModalService.showCompletionMethodsForCourseUnit(courseUnit.id, validatablePlan, `cu-color-${colorCategory}`);
    }
}
